// ── Qué era nuevo en esta visita ─────────────────────────────────────────────
// El badge se apaga apenas entrás al módulo. Si eso fuera todo, en el mismo
// instante se perdería CUÁL era la novedad — el número baja y no sabés qué
// mirar. Acá se guarda la marca de agua ANTERIOR: la lista compara contra ella
// y resalta las filas que llegaron desde la última vez.
//
// Solo hace falta para Proyectos y Reportes, donde "pendiente" no es un estado
// que la fila lleve encima. En los demás módulos la fila ya sabe si espera algo
// (una respuesta sin contestar, una factura vencida, una tarea sin aprobar) y
// se marca sola, sin depender de cuándo miraste.
//
// Desde el 2026-10-12 también se marca a mano, leído o no leído, desde la
// barra de acciones de la lista. Un "no leído" a mano sobrevive a la marca de
// agua (ver 0162_nav_item_unread.sql) hasta que se abre o se marca leído.
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavSeenTracker, type MarcaDeModulo } from '../hooks/useNavSeen.js';
import { useMarkNavItemSeen, useMarkNavItems } from '../hooks/useForms.js';
import type { NavModule } from '../hooks/useForms.js';

interface Valor {
  marcas: Record<string, MarcaDeModulo>;
  /** Lo marcado en ESTA sesión, por ítem: true = leído, false = no leído. Gana
   *  sobre lo que trajo el servidor al entrar, que puede ser anterior. */
  locales: Record<string, Map<string, boolean>>;
  marcarItem: (module: NavModule, itemId: string) => void;
  marcarItems: (module: NavModule, itemIds: string[], leido: boolean) => Promise<void>;
}

const NavSeenCtx = createContext<Valor>({
  marcas: {}, locales: {}, marcarItem: () => undefined, marcarItems: () => Promise.resolve(),
});

export function NavSeenProvider({ children }: { children: ReactNode }) {
  const marcas = useNavSeenTracker();
  const qc = useQueryClient();
  const marcarEnServidor = useMarkNavItemSeen();
  const marcarVariosEnServidor = useMarkNavItems();
  const [locales, setLocales] = useState<Record<string, Map<string, boolean>>>({});

  const anotar = useCallback((module: string, itemIds: string[], leido: boolean) => {
    setLocales(prev => {
      const copia = new Map(prev[module] ?? []);
      for (const id of itemIds) copia.set(id, leido);
      return { ...prev, [module]: copia };
    });
  }, []);

  // Se apaga en pantalla al instante y se persiste en paralelo: esperar la
  // respuesta para quitar el resaltado se ve como que el clic no hizo nada.
  const marcarItem = useCallback((module: NavModule, itemId: string) => {
    if (locales[module]?.get(itemId) === true) return;
    // Abrir uno marcado "no leído" a mano lo descuenta del menú: ahí sí hay
    // que refrescar el badge. Abrir una novedad común no lo mueve.
    const eraNoLeido = locales[module]?.get(itemId) === false || (marcas[module]?.noLeidos.has(itemId) ?? false);
    anotar(module, [itemId], true);
    marcarEnServidor.mutate({ module, itemId }, {
      onSuccess: () => { if (eraNoLeido) void qc.invalidateQueries({ queryKey: ['nav-badges'] }); },
    });
  // `marcarEnServidor` es estable entre renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locales, marcas, anotar, qc]);

  const marcarItems = useCallback(async (module: NavModule, itemIds: string[], leido: boolean) => {
    const previo = locales[module];
    anotar(module, itemIds, leido);
    try {
      await marcarVariosEnServidor.mutateAsync({ module, itemIds, leido });
    } catch (err) {
      // Si no se guardó, la pantalla no puede quedar diciendo que sí.
      setLocales(prev => ({ ...prev, [module]: new Map(previo ?? []) }));
      throw err;
    }
  // `marcarVariosEnServidor` es estable entre renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locales, anotar]);

  return <NavSeenCtx.Provider value={{ marcas, locales, marcarItem, marcarItems }}>{children}</NavSeenCtx.Provider>;
}

/** Marca un ítem como mirado. Apaga SU resaltado, no el del módulo. */
export function useMarcarItemVisto(module: NavModule): (itemId: string | number) => void {
  const { marcarItem } = useContext(NavSeenCtx);
  return useCallback((itemId) => { marcarItem(module, String(itemId)); }, [marcarItem, module]);
}

/** Leído / no leído para varios ítems — la barra de acciones de una lista. */
export function useMarcarItemsLeidos(module: NavModule): (itemIds: Array<string | number>, leido: boolean) => Promise<void> {
  const { marcarItems } = useContext(NavSeenCtx);
  return useCallback(
    (itemIds, leido) => marcarItems(module, itemIds.map(String), leido),
    [marcarItems, module],
  );
}

/**
 * Predicado "esto es nuevo desde la última vez que miré", para el módulo dado.
 *
 * Devuelve `false` mientras la marca no llegó: preferir no marcar nada antes
 * que marcar TODO por un instante. Un parpadeo en el que la lista entera se ve
 * nueva es peor que esperar medio segundo.
 */
export function useEsNuevo(module: string): (fecha: string | number | null | undefined, itemId?: string | number) => boolean {
  const { marcas, locales } = useContext(NavSeenCtx);
  const marca = marcas[module];
  const local = locales[module];
  return (fecha, itemId) => {
    if (itemId !== undefined) {
      const id = String(itemId);
      // Lo marcado a mano en esta sesión manda sobre todo lo demás.
      const aMano = local?.get(id);
      if (aMano !== undefined) return !aMano;
      if (marca?.noLeidos.has(id)) return true;
      // Mirado = ya no es novedad, aunque sea posterior a la marca de agua.
      if (marca?.vistos.has(id)) return false;
    }
    if (marca === undefined || fecha == null) return false;
    const t = typeof fecha === 'number' ? fecha : Date.parse(fecha);
    if (Number.isNaN(t)) return false;
    return t > Date.parse(marca.previo);
  };
}
